import React, {
  createContext,
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from './AuthContext';
import NotificationService from '../services/NotificationService';
import { fetchHomeBootSlice } from '../services/HomeBootService';
import queryKeys from '../queries/queryKeys';
import useQueryScope from '../queries/useQueryScope';
import { readOfflineSnapshot, writeOfflineSnapshot, isAccessDenied } from '../queries/queryOwnership';
import { captureQueryProjection, getQueryProjectionRevision, revokeQueryProjection } from '../queries/queryProjectionRevision';
import { initializeSocket, resetSharedSocket } from '../services/SocketService';
import { subscribeNotificationRefresh } from '../services/notificationRefresh';

const defaultNotificationsContext = {
  notifications: [],
  setNotifications: () => {},
  fetchNotifications: async () => null,
  markNotificationRead: async () => null,
  markAllNotificationsRead: async () => null,
  loading: false,
  error: null,
  lastFetchedAt: 0,
};

const NotificationsContext = createContext(defaultNotificationsContext);

export const useNotificationsContext = () => useContext(NotificationsContext);

const mapNotificationCollection = (collection, mapper) => {
  if (Array.isArray(collection)) {
    return collection.map(mapper);
  }

  if (Array.isArray(collection?.data)) {
    return {
      ...collection,
      data: collection.data.map(mapper),
    };
  }

  return collection;
};

const NOTIFICATIONS_TTL_MS = 45_000;


export const NotificationsProvider = ({ children }) => {
  const { user, token } = useAuthContext();
  const queryClient = useQueryClient();
  const userId = user?.id || user?._id || user?.userId || null;
  const { scope, scopeId, isCurrent } = useQueryScope();
  const activeSession = useRef({ userId, token, scopeId });
  activeSession.current = { userId, token, scopeId };
  const pendingReads = useRef(new Map());
  const initialPersistedNotifications = useMemo(
    () => readOfflineSnapshot('notifications', scope, []), [scope],
  );
  const queryKey = useMemo(() => queryKeys.notifications.byUser(scope), [scope]);
  const projectionRevision = getQueryProjectionRevision(queryClient, queryKey);
  const persistedNotificationsRef = useRef(initialPersistedNotifications);

  useEffect(() => {
    persistedNotificationsRef.current = initialPersistedNotifications;
  }, [initialPersistedNotifications]);

  const setPersistedNotifications = useCallback((nextNotifications) => {
    if (!isCurrent()) return;
    persistedNotificationsRef.current = nextNotifications;
    writeOfflineSnapshot('notifications', scope, nextNotifications);
  }, [isCurrent, scope]);

  const notificationsQuery = useQuery({
    queryKey,
    enabled: Boolean(userId && token),
    queryFn: () => fetchHomeBootSlice(
      'notifications',
      { token, userId },
      () => NotificationService.getAllNotifications(userId, {}, { token }),
    ),
    initialData: initialPersistedNotifications,
    initialDataUpdatedAt: 0,
    staleTime: NOTIFICATIONS_TTL_MS,
  });

  // initialData is adopted once by the query. Falling back to its old memo
  // after a denied read would resurrect that private seed on a later 503.
  const notifications = useMemo(() => isAccessDenied(notificationsQuery.error) ? [] : (notificationsQuery.data ?? []),
    [notificationsQuery.error, notificationsQuery.data]);
  const notificationsRef = useRef(notifications);
  const refScope = useRef(scopeId);
  if (refScope.current !== scopeId) {
    refScope.current = scopeId;
    notificationsRef.current = notifications;
    persistedNotificationsRef.current = initialPersistedNotifications;
  }

  useEffect(() => {
    notificationsRef.current = notifications;
    persistedNotificationsRef.current = notifications;
    setPersistedNotifications(notifications);
  }, [notifications, setPersistedNotifications]);

  const setNotifications = useCallback((nextNotifications) => {
    if (!isCurrent() || projectionRevision !== getQueryProjectionRevision(queryClient, queryKey)) return;
    const resolved = typeof nextNotifications === 'function'
      ? nextNotifications(notificationsRef.current)
      : nextNotifications;

    void queryClient.cancelQueries({ queryKey });
    notificationsRef.current = resolved;
    persistedNotificationsRef.current = resolved;
    queryClient.setQueryData(queryKey, resolved);
    setPersistedNotifications(resolved);
  }, [queryClient, queryKey, setPersistedNotifications, isCurrent, projectionRevision]);

  const fetchNotifications = useCallback(async ({
    force = false,
    token: tokenOverride,
    userId: userIdOverride,
  } = {}) => {
    const authToken = tokenOverride || token;
    const resolvedUserId = userIdOverride || userId;
    if (!isCurrent() || resolvedUserId !== userId || authToken !== token) return [];
    if (!resolvedUserId || !authToken) return notificationsRef.current;

    const resolvedQueryKey = queryKeys.notifications.byUser(scope);
    if (force) {
      // The fetch below owns the refresh; invalidation must not start another one.
      await queryClient.invalidateQueries({ queryKey: resolvedQueryKey, refetchType: 'none' });
    }

    return queryClient.fetchQuery({
      queryKey: resolvedQueryKey,
      staleTime: force ? 0 : NOTIFICATIONS_TTL_MS,
      queryFn: () => NotificationService.getAllNotifications(
        resolvedUserId,
        {},
        { token: authToken },
      ),
    });
  }, [queryClient, token, userId, scope, isCurrent]);

  useEffect(() => {
    if (!userId || !token) {
      resetSharedSocket();
      return undefined;
    }
    const refresh = () => {
      if (!isCurrent()) return;
      void fetchNotifications({ force: true }).catch(() => {
        // The shared query exposes refresh errors and retains its retry policy.
      });
    };
    const socket = initializeSocket(token);
    socket?.on('notifications-changed', refresh);
    socket?.on('connect', refresh);
    const unsubscribe = subscribeNotificationRefresh(refresh);
    return () => {
      socket?.off('notifications-changed', refresh);
      socket?.off('connect', refresh);
      unsubscribe();
    };
  }, [fetchNotifications, isCurrent, token, userId]);

  const { mutateAsync: markAllRead } = useMutation({
    mutationFn: ({ token: authToken, userId: resolvedUserId, ownsOperation }) => {
      if (!ownsOperation()) return null;
      return NotificationService.markAllNotificationsAsRead(resolvedUserId, { token: authToken });
    },
    onMutate: async ({ userId: resolvedUserId, token: authToken, ownsOperation, operationKey: resolvedQueryKey, operationScopeId }) => {
      await queryClient.cancelQueries({ queryKey: resolvedQueryKey });

      if (!ownsOperation() || activeSession.current.token !== authToken || activeSession.current.userId !== resolvedUserId) return null;
      const previousNotifications =
        queryClient.getQueryData(resolvedQueryKey) ?? persistedNotificationsRef.current;
      const nextNotifications = mapNotificationCollection(previousNotifications, (notification) => ({
        ...(notification || {}),
        isRead: true,
      }));

      notificationsRef.current = nextNotifications;
      queryClient.setQueryData(resolvedQueryKey, nextNotifications);
      if (String(resolvedUserId) === String(userId)) {
        persistedNotificationsRef.current = nextNotifications;
        setPersistedNotifications(nextNotifications);
      }

      return { previousNotifications, resolvedQueryKey, resolvedUserId, authToken, scopeId: operationScopeId, ownsOperation };
    },
    onError: (error, _variables, context) => {
      if (!context || !context.ownsOperation() || activeSession.current.scopeId !== context.scopeId || !isCurrent()) return;
      if (isAccessDenied(error)) {
        revokeQueryProjection(queryClient, context.resolvedQueryKey);
        queryClient.setQueryData(context.resolvedQueryKey, []);
        setPersistedNotifications([]);
        return;
      }
      queryClient.setQueryData(context.resolvedQueryKey, context.previousNotifications);
      if (String(context.resolvedUserId) === String(userId)) {
        notificationsRef.current = context.previousNotifications;
        persistedNotificationsRef.current = context.previousNotifications;
        setPersistedNotifications(context.previousNotifications);
      }
    },
  });

  const { mutateAsync: markRead } = useMutation({
    mutationFn: ({ notificationId, token: authToken, ownsOperation }) =>
      ownsOperation() ? NotificationService.markNotificationAsRead(notificationId, { token: authToken }) : null,
    onMutate: async ({ notificationId, userId: resolvedUserId, token: authToken, ownsOperation, operationKey: resolvedQueryKey, operationScopeId }) => {
      await queryClient.cancelQueries({ queryKey: resolvedQueryKey });
      if (!ownsOperation() || activeSession.current.userId !== resolvedUserId || activeSession.current.token !== authToken) return null;

      const previous = queryClient.getQueryData(resolvedQueryKey) ?? notificationsRef.current;
      let previousNotification;
      const next = mapNotificationCollection(previous, (notification) => {
        if (String(notification?._id || notification?.id) !== notificationId) return notification;
        previousNotification = notification;
        return { ...notification, isRead: true };
      });
      notificationsRef.current = next;
      queryClient.setQueryData(resolvedQueryKey, next);
      setPersistedNotifications(next);
      return { previousNotification, resolvedQueryKey, resolvedUserId, authToken, scopeId: operationScopeId, ownsOperation };
    },
    onError: (error, { notificationId }, context) => {
      if (!context?.previousNotification || !context.ownsOperation() || !isCurrent() || activeSession.current.scopeId !== context.scopeId
        || activeSession.current.userId !== context.resolvedUserId
        || activeSession.current.token !== context.authToken) return;
      if (isAccessDenied(error)) {
        revokeQueryProjection(queryClient, context.resolvedQueryKey);
        queryClient.setQueryData(context.resolvedQueryKey, []);
        setPersistedNotifications([]);
        return;
      }
      // Roll back only this item so another successful read is preserved.
      const next = mapNotificationCollection(queryClient.getQueryData(context.resolvedQueryKey), (notification) => (
        String(notification?._id || notification?.id) === notificationId
          ? { ...notification, isRead: context.previousNotification.isRead }
          : notification
      ));
      notificationsRef.current = next;
      queryClient.setQueryData(context.resolvedQueryKey, next);
      setPersistedNotifications(next);
    },
  });

  const markNotificationRead = useCallback((id) => {
    const notificationId = String(id || '');
    if (!isCurrent() || !notificationId || !userId || !token) return Promise.resolve();
    const pendingKey = JSON.stringify([scopeId, notificationId]);
    if (pendingReads.current.has(pendingKey)) return pendingReads.current.get(pendingKey);
    const collection = notificationsRef.current;
    const items = Array.isArray(collection) ? collection : collection?.data || [];
    if (items.find(item => String(item?._id || item?.id) === notificationId)?.isRead) return Promise.resolve();

    const projectionIsCurrent = captureQueryProjection(queryClient, queryKey);
    const ownsOperation = () => isCurrent() && projectionIsCurrent();
    const request = markRead({ notificationId, userId, token, ownsOperation, operationKey: queryKey, operationScopeId: scopeId }).finally(() => {
      pendingReads.current.delete(pendingKey);
    });
    pendingReads.current.set(pendingKey, request);
    return request;
  }, [markRead, token, userId, isCurrent, queryClient, queryKey, scopeId]);

  const markAllNotificationsRead = useCallback(async ({
    token: tokenOverride,
    userId: userIdOverride,
  } = {}) => {
    const authToken = tokenOverride || token;
    const resolvedUserId = userIdOverride || userId;
    if (!isCurrent() || resolvedUserId !== userId || authToken !== token) return [];
    if (!resolvedUserId || !authToken) return notificationsRef.current;

    const projectionIsCurrent = captureQueryProjection(queryClient, queryKey);
    const ownsOperation = () => isCurrent() && projectionIsCurrent();
    await markAllRead({
      ownsOperation, operationKey: queryKey, operationScopeId: scopeId,
      token: authToken,
      userId: resolvedUserId,
    });
    return notificationsRef.current;
  }, [markAllRead, token, userId, isCurrent, queryClient, queryKey, scopeId]);

  const value = useMemo(
    () => ({
      notifications,
      setNotifications,
      fetchNotifications,
      markNotificationRead,
      markAllNotificationsRead,
      loading: notificationsQuery.isLoading,
      error: notificationsQuery.error?.message || null,
      lastFetchedAt: notificationsQuery.dataUpdatedAt || 0,
    }),
    [
      fetchNotifications,
      markNotificationRead,
      markAllNotificationsRead,
      notifications,
      notificationsQuery.dataUpdatedAt,
      notificationsQuery.error?.message,
      notificationsQuery.isLoading,
      setNotifications,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
